
import { supabase } from '../../supabase';

/**
 * Gets the public URL for a file in Supabase Storage
 * @param filePath Path to the file in storage
 * @returns Public URL for the file
 */
export function getPublicUrl(filePath: string): string {
  if (!filePath) {
    console.error('Invalid file path provided to getPublicUrl:', filePath);
    return '';
  }

  try {
    const { data: publicUrl } = supabase.storage
      .from('papers')
      .getPublicUrl(filePath);
    
    console.log('Public URL for file:', publicUrl.publicUrl);
    return publicUrl.publicUrl;
  } catch (error) {
    console.error('Error generating public URL:', error);
    return '';
  }
}

/**
 * Converts a public URL back to a storage path
 * @param publicUrl Public URL of the file
 * @returns Storage path for the file
 */
export function getPathFromUrl(publicUrl: string): string | null {
  try {
    if (!publicUrl) return null;
    
    // Extract the path from the public URL
    // Format is typically: https://[project-ref].supabase.co/storage/v1/object/public/papers/[userId]/[fileName]
    const url = new URL(publicUrl);
    const pathParts = url.pathname.split('/');
    const bucketIndex = pathParts.findIndex(part => part === 'papers');
    
    if (bucketIndex === -1 || bucketIndex >= pathParts.length - 1) {
      console.error('Invalid URL format:', publicUrl);
      return null;
    }
    
    // Combine the relevant parts to form the storage path
    return pathParts.slice(bucketIndex + 1).join('/');
  } catch (error) {
    console.error('Error parsing file URL:', error);
    return null;
  }
}

/**
 * Checks if a URL is a valid Supabase storage URL
 * @param url URL to check
 * @returns boolean indicating if this is a valid storage URL
 */
export function isValidStorageUrl(url: string): boolean {
  if (!url) return false;
  
  try {
    const parsedUrl = new URL(url);
    
    // Check if it's a blob URL (which is invalid for storage)
    if (parsedUrl.protocol === 'blob:') {
      console.error('Blob URL detected, not a valid storage URL:', url);
      return false;
    }
    
    // Check if it's a valid paper URL (Supabase storage or arXiv)
    const isSupabaseStorage =
      parsedUrl.hostname.includes('supabase.co') &&
      parsedUrl.pathname.includes('/storage/v1/object/public/papers/');

    const isArxivUrl = parsedUrl.hostname.includes('arxiv.org');

    if (!isSupabaseStorage && !isArxivUrl) {
      console.error('Not a valid paper URL:', url);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating storage URL:', error, 'URL:', url);
    return false;
  }
}
